import {
  AudienceProps,
  AudienceType,
  Clazz,
  Level,
  School,
  ScopeConstraints,
  Student,
  StudentEntityType,
  StudentRoster,
  TeachingGroup
} from 'components/models/FormProps';

type StudentAudienceEntity = School | Level | Clazz | TeachingGroup | Student;

// The template's scope constraint applied to the roster the selector offers, so
// that a form creator on a constrained template has nothing to click that would
// violate it.
//
// The roster is fetched whole, exactly as it is today, and narrowed here at the
// point of rendering: the constraint is a fact about the form's template, and a
// generic roster endpoint has no business knowing about templates. An absent
// field is unrestricted, matching how the gates read the same payload; an empty
// list is not, and admits nobody.
export function scopeStudentRoster(roster: StudentRoster, scopeConstraints: ScopeConstraints | null): StudentRoster {
  const { allowedLevelCodes } = scopeConstraints ?? {};
  if (allowedLevelCodes == null) return roster;

  const allowedCodes = new Set(allowedLevelCodes);

  // Every roster entity below the school carries its level code, so the whole
  // narrowing is one membership check. A student the roster has no level for
  // serialises without one, has no code to match, and so falls out: under a
  // level constraint they reach nobody in scope. The authoring gate permits such
  // a row rather than blocking a save on it; the selector still has no reason to
  // offer it.
  return {
    ...roster,
    levels: roster.levels.filter(level => allowedCodes.has(level.code)),
    clazzes: roster.clazzes.filter(clazz => allowedCodes.has(clazz.level_code)),
    teachingGroups: roster.teachingGroups.filter(teachingGroup => allowedCodes.has(teachingGroup.level_code)),
    students: roster.students.filter(student => allowedCodes.has(student.level_code))
  };
}

// Whether to offer a whole school as an audience at all - both through the
// school's own selector and through the HQ school picker, which reach the same
// entity by different routes. It is hidden rather than shown-and-refused, so the
// constraint reads as a narrower set of choices rather than as a trap.
//
// This mirrors the authoring gate's judgement of a stored school entity exactly,
// so that the two can never disagree about a button the gate would refuse.
export function allowsSchoolWideSelection(scopeConstraints: ScopeConstraints | null): boolean {
  const { allowedEntityTypes, allowedLevelCodes } = scopeConstraints ?? {};

  if (allowedEntityTypes != null && !allowedEntityTypes.includes(StudentEntityType.SCHOOL)) return false;

  // A school's levellessness is its nature: it spans every level, so it can never
  // sit inside a level constraint, whatever entity types are permitted.
  return allowedLevelCodes == null;
}

// Whether a selection already in the audience is one the constraint disallows.
//
// A saved audience can hold entities the constraint now refuses: templates are
// patched after forms exist, and rosters roll over every academic year. Those
// selections are left visible and flagged rather than silently stripped, so the
// system never changes who receives a form without telling the form creator.
//
// The panel judges its own rows because it is the only place holding hydrated
// entities: the authoring gate's rejection names entity types and level codes,
// which cannot pick a row out of many. That makes this a second copy of the
// gate's judgement, so it mirrors it exactly - including its forgiveness of a
// levelless row, which the gate permits and which must therefore not be flagged
// as something to clear before a save the server would have accepted.
export function isEntityOutOfScope(entity: StudentAudienceEntity, scopeConstraints: ScopeConstraints | null): boolean {
  const { allowedEntityTypes, allowedLevelCodes } = scopeConstraints ?? {};

  if (allowedEntityTypes != null && !allowedEntityTypes.includes(entity.entity_type as StudentEntityType)) return true;

  if (entity.entity_type === StudentEntityType.SCHOOL) return allowedLevelCodes != null;

  if (allowedLevelCodes == null) return false;

  const levelCode = levelCodeOf(entity);
  if (levelCode == null) return false;

  return !allowedLevelCodes.includes(levelCode);
}

// Whether the audience holds any selection the form creator has to clear before
// the authoring gate will accept a save. Audiences the constraint has no
// vocabulary for - staff, public, and the untargeted audiences, which name no
// entities at all - are not judged here, matching the gate exactly.
export function hasOutOfScopeEntities(audience: AudienceProps, scopeConstraints: ScopeConstraints | null): boolean {
  if (audience.type !== AudienceType.STUDENT && audience.type !== AudienceType.PARENT) return false;

  return audience.entities.some(entity => isEntityOutOfScope(entity, scopeConstraints));
}

// A level carries its own code; everything below one carries the code of the
// level it sits in. A school has none, and is judged above.
function levelCodeOf(entity: StudentAudienceEntity): number | null {
  if (entity.entity_type === StudentEntityType.LEVEL) return (entity as Level).code;

  return (entity as Clazz | TeachingGroup | Student).level_code;
}
